import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { InterviewInfoComponent } from './interview-info/interview-info.component';
import { SituationReviewComponent } from './situation-review/situation-review.component';
import { ImpactComponent } from "./impact/impact.component";
import { GeneralBehaviorComponent } from "./general-behavior/general-behavior.component";
import { GeneralKnowledgeComponent } from "./general-knowledge/general-knowledge.component";
import { SupportResourcesComponent } from './support-resources/support-resources.component';
import { EmotionalReactionComponent } from './emotional-reaction/emotional-reaction.component';
import { StudentInterviewSubmissionComponent } from './student-interview-submission/student-interview-submission.component';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { StudentOfConcernInterviewService } from '../../../../shared/services/studentOfConcernInterview.service';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'student-of-concern-interview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    InterviewInfoComponent,
    SituationReviewComponent,
    ImpactComponent,
    SupportResourcesComponent,
    GeneralBehaviorComponent,
    EmotionalReactionComponent,
    GeneralKnowledgeComponent,
    StudentInterviewSubmissionComponent,
    PopupMessageComponent,
    SpinnerComponent
],
  templateUrl: './student-of-concern-interview.component.html',
  styleUrl: './student-of-concern-interview.component.css'
})
export class StudentOfConcernInterviewComponent {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentStudentInterview: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'student_interview_info_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'situation_review_section_completion',
      nextStepDependency: 'student_interview_info_section_completion'
    },
    {
      id: 3,
      completionProperty: 'impacts_section_completion',
      nextStepDependency: 'situation_review_section_completion'
    },
    {
      id: 4,
      completionProperty: 'student_summary_section_completion',
      nextStepDependency: 'impacts_section_completion'
    },
    {
      id: 5,
      completionProperty: 'appearance_section_completion',
      nextStepDependency: 'student_summary_section_completion'
    },
    {
      id: 6,
      completionProperty: 'emotional_section_completion',
      nextStepDependency: 'appearance_section_completion'
    },
    {
      id: 7,
      completionProperty: 'knowledge_section_completion',
      nextStepDependency: 'emotional_section_completion'
    }
  ];
  finalStepConfig = {
      id: 8,
      completionProperty: 'student_of_concern_interview_completion',
      nextStepDependency: 'knowledge_section_completion'
  };
  currentStep: number = 1;
  tooltips = ['Interview Information', 'Situation Review', 'Impact on Others', 'Support Resources/Summary', 'General Observations', 'Emotional Reaction', 'General Knowledge', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private studentOfConcernInterviewService: StudentOfConcernInterviewService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadStudentInterview();
  }

  loadStudentInterview(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.studentOfConcernInterviewService.getStudentOfConcernInterviewByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (response) {
            this.currentStudentInterview = response;
          } else {
            this.currentStudentInterview = null;
          }
          this.loading = false;
          return
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;  
        }
      });
    }
  }


  handleUpdateStudentInterviewData(data: any, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.studentOfConcernInterviewService.updateStudentOfConcernInterview(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadStudentInterview();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 8 ) {
            this.router.navigate(['/assessments/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating student of concern interview:', error.message);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/assessments/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentStudentInterview) {
      return this.currentStudentInterview?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
